import { Button, CloseButton, Dialog, Portal, Stack, Text, Code, Flex } from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useEventTypes } from '../contexts/EventTypesContext'
import { updateEvent } from '../services/firebaseService'
import { slugForType, storedWebappUrl } from '../utils/webappUrl'
import { toaster } from './ui/toaster'
import { Tooltip } from './ui/tooltip'
import { useTranslation } from '../i18n'

function fmt(v) {
  if (v === undefined) return '—'
  if (v === null) return 'null'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

// Content fields belong to the event, not the type. A type change must never
// overwrite them, even if a type's `defaults` happens to include the key.
const CONTENT_FIELDS = new Set([
  'name', 'challenges', 'backgroundUrl', 'logoUrl', 'prizeBackgroundUrl',
  'splashUrl', 'uploadBackgroundUrl', 'challengePrompt', 'prizePrompt', 'validatorCodes',
])

// Modal: confirm changing an event's type. Shows the eventType change, the
// URL slug change, and every preset-managed field the target type's defaults
// would overwrite. Two ways to confirm: change the type only (eventType +
// webappUrl), or change it and apply those defaults too. User-authored content
// is preserved either way.
export default function ChangeEventTypeDialog({ open, onClose, eventId, eventData, currentType, nextType, onChanged }) {
  const { t, lang } = useTranslation()
  const { getTypeConfig } = useEventTypes()
  // Which confirm button is in flight: 'only' | 'defaults' | null.
  const [saving, setSaving] = useState(null)

  const cur = getTypeConfig(currentType)
  const next = getTypeConfig(nextType)

  const changes = useMemo(() => {
    const defaults = next?.defaults || {}
    const rows = []
    for (const key of Object.keys(defaults)) {
      if (CONTENT_FIELDS.has(key)) continue
      const before = eventData ? eventData[key] : undefined
      const after = defaults[key]
      if (JSON.stringify(before) !== JSON.stringify(after)) {
        rows.push({ key, before, after })
      }
    }
    return rows
  }, [next, eventData])

  const handleConfirm = async (applyDefaults) => {
    try {
      setSaving(applyDefaults ? 'defaults' : 'only')
      const updates = { eventType: nextType }
      if (applyDefaults) for (const row of changes) updates[row.key] = row.after
      // The webapp URL is derived from the type's slug, so it must be recomputed
      // whenever the type (and therefore the slug) changes.
      updates.webappUrl = storedWebappUrl(slugForType(next, nextType), eventId)
      await updateEvent(eventId, updates)
      toaster.create({ title: t('eventTypes.changed'), status: 'success' })
      onChanged?.(nextType)
      onClose?.()
    } catch (err) {
      console.error(err)
      toaster.create({ title: t('eventTypes.changeFailed'), description: err.message, status: 'error' })
    } finally {
      setSaving(null)
    }
  }

  const label = (cfg, id) => cfg?.label?.[lang] || cfg?.label?.en || id

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open && !saving) onClose?.() }} role="alertdialog">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{t('eventTypes.changeTitle')}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap={3}>
                <Text>
                  {t('eventTypes.changeType')}: <Code>{label(cur, currentType)}</Code> → <Code>{label(next, nextType)}</Code>
                </Text>
                <Text>
                  {t('eventTypes.changeUrl')}: <Code>/{cur?.urlSlug || currentType}/…</Code> → <Code>/{next?.urlSlug || nextType}/…</Code>
                </Text>
                {changes.length > 0 ? (
                  <Stack gap={1}>
                    <Text fontWeight="600">{t('eventTypes.changeFields')}</Text>
                    {changes.map((row) => (
                      <Flex key={row.key} gap={2} wrap="wrap" fontSize="sm">
                        <Code>{row.key}</Code>
                        <Text as="span">{fmt(row.before)} → {fmt(row.after)}</Text>
                      </Flex>
                    ))}
                  </Stack>
                ) : (
                  <Text fontSize="sm" color="fg.muted">{t('eventTypes.changeNoFields')}</Text>
                )}
                <Text fontSize="sm" color="fg.muted">{t('eventTypes.changePreserveNote')}</Text>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer flexWrap="wrap">
              <Button variant="outline" disabled={!!saving} onClick={() => onClose?.()}>{t('common.cancel')}</Button>
              <Tooltip showArrow content={t('eventTypes.changeTypeOnlyTip')} contentProps={{ maxW: '320px' }}>
                <Button
                  variant="outline"
                  loading={saving === 'only'}
                  disabled={!!saving}
                  loadingText={t('common.saving')}
                  onClick={() => handleConfirm(false)}
                >
                  {t('eventTypes.changeTypeOnly')}
                </Button>
              </Tooltip>
              <Tooltip
                showArrow
                contentProps={{ maxW: '360px' }}
                content={changes.length > 0 ? (
                  <Stack gap={1}>
                    <Text>{t('eventTypes.changeApplyDefaultsTip')}</Text>
                    {changes.map((row) => (
                      <Text key={row.key} fontFamily="mono" fontSize="xs">
                        {row.key}: {fmt(row.before)} → {fmt(row.after)}
                      </Text>
                    ))}
                  </Stack>
                ) : t('eventTypes.changeApplyDefaultsNone')}
              >
                {/* A disabled button fires no pointer events, so the span keeps the tooltip reachable. */}
                <span>
                  <Button
                    colorPalette="brand"
                    loading={saving === 'defaults'}
                    disabled={!!saving || changes.length === 0}
                    loadingText={t('common.saving')}
                    onClick={() => handleConfirm(true)}
                  >
                    {t('eventTypes.changeApplyDefaults')}
                  </Button>
                </span>
              </Tooltip>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" disabled={!!saving} />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
